import React from "react";
import { useAtomValue } from "jotai";
import { useTranslation } from "react-i18next";
import { tasksAtom } from "../../../../../atoms/v0/taskAtoms";
import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  isPast,
} from "date-fns";
import ClockAreaFooter from "./ClockAreaFooter";

interface TaskCountdownAreaProps {
  taskId?: string;
  currentTime: Date;
  countdownBase?: "start" | "due" | "end";
  onEditArea?: () => void; // 可选的编辑区域回调
}

/**
 * 任务倒数日组件
 */
export const TaskCountdownArea: React.FC<TaskCountdownAreaProps> = ({
  taskId,
  currentTime,
  countdownBase = "due",
  onEditArea,
}) => {
  const { t } = useTranslation(["v3/clock"]);
  const tasks = useAtomValue(tasksAtom);

  const task = taskId ? tasks.find((t) => t.id === taskId) : null;

  if (!task) {
    return (
      <div
        className="h-full w-full flex flex-col items-center justify-center cursor-pointer"
        onClick={onEditArea}
      >
        <div className="grow h-1 flex justify-center items-center">
          <button className="action-button">
            <span className="text-blue-500 font-bold">{"选择任务…"}</span>
          </button>
        </div>
        <ClockAreaFooter text="倒数日" icon={"⏰"} />
      </div>
    );
  }

  // 根据基准时间选择目标日期
  const getTargetDate = () => {
    switch (countdownBase) {
      case "start":
        return task.startAt ? new Date(task.startAt) : null;
      case "end":
        return task.endAt ? new Date(task.endAt) : null;
      case "due":
      default:
        return task.dueAt ? new Date(task.dueAt) : null;
    }
  };

  const targetDate = getTargetDate();

  if (!targetDate) {
    const baseLabels = {
      start: t("v3/clock:components.taskCountdown.noStartDate", "无开始时间"),
      end: t("v3/clock:components.taskCountdown.noEndDate", "无结束时间"),
      due: t("v3/clock:components.taskCountdown.noDueDate", "无截止时间"),
    };

    return (
      <div
        className="h-full w-full flex flex-col items-center justify-center cursor-pointer"
        onClick={onEditArea}
      >
        <div className="grow h-1 flex flex-col gap-[6px] items-center justify-center">
          <div className="center-tips">{"选择的任务时间无效"}</div>
          <button className="action-button">
            <span className="text-blue-500 font-bold">{"设置…"}</span>
          </button>
        </div>
        <ClockAreaFooter text="倒数日" icon={"⏰"} />
      </div>
    );
  }

  const isOverdue = isPast(targetDate);

  // 计算时间差
  const days = Math.abs(differenceInDays(targetDate, currentTime));
  const hours = Math.abs(differenceInHours(targetDate, currentTime)) % 24;
  const minutes = Math.abs(differenceInMinutes(targetDate, currentTime)) % 60;

  // 格式化显示
  const getTimeDisplay = () => {
    if (days > 0) {
      return [days, "DAYS"]; // `${days}天${hours}时`;
    } else if (hours > 0) {
      return [hours, "HOURS"]; // `${hours}时${minutes}分`;
    } else {
      return [minutes, "MINS"]; // `${minutes}分钟`;
    }
  };

  return (
    <div className="h-full w-full flex flex-col items-center justify-center cursor-pointer" onClick={onEditArea}>
      <div className="grow h-1 flex flex-col items-center justify-center gap-[6px]">
        {/* 倒计时显示 */}
        <div className="text-center">
          <div
            className={`clock-digit-font font-bold leading-none ${
              null
              // isOverdue
              //   ? "text-red-500 dark:text-red-400"
              //   : days <= 1
              //     ? "text-orange-500 dark:text-orange-400"
              //     : "text-blue-500 dark:text-blue-400"
            }`}
          >
            <span className="text-[2.5em]">{getTimeDisplay()[0]}</span> {getTimeDisplay()[1]}
          </div>

          <div className="flex gap-[6px] items-center justify-start">
            <div
              className={`text-[10px] truncate opacity-45 ${
                null
                // isOverdue
                //   ? "text-red-400 dark:text-red-500"
                //   : "text-gray-500 dark:text-gray-400"
              }`}
            >
              {isOverdue
                ? t("v3/clock:components.taskCountdown.overdue", "已逾期")
                : countdownBase === "start"
                  ? t("v3/clock:components.taskCountdown.toStart", "距开始")
                  : countdownBase === "end"
                    ? t("v3/clock:components.taskCountdown.toEnd", "距结束")
                    : t(
                        "v3/clock:components.taskCountdown.remaining",
                        "剩余时间"
                      )}
            </div>

            <div className="h-[8px] w-[1px] bg-[var(--cm-comp-text-color)] opacity-20"></div>
                      
            {/* rounded-full bg-[var(--cm-comp-bg-secondary)] px-[8px] py-[2px]  */}
            <div className="text-[10px] truncate text-center text-ellipsis opacity-45">
              {task.title}
            </div>
          </div>
        </div>
      </div>

      <ClockAreaFooter text="倒数日" icon={"⏰"} />
    </div>
  );
};

export default TaskCountdownArea;
